import type { AleResult } from '@val/shared'
import { ALE_2022_VOTES_BY_CODE, PARTIES } from '@val/shared'

export type BaseKind = 'live' | 'val-2022' | 'blank'

export interface Scenario {
  id: string
  name: string
  baseKind: BaseKind
  /** Absolut röstetal per parti. Detta är sanningen scenariot räknar på. */
  votes: Record<string, number>
  /** Röstetal i basen, för "ändring mot utgångsläget". */
  baseVotes: Record<string, number>
  updatedAt: string
}

const STORAGE_KEY = 'val.scenarios.v1'

export function newId(): string {
  return `s_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`
}

export function votesFromResult(result: AleResult): Record<string, number> {
  const out: Record<string, number> = {}
  for (const p of result.parties) out[p.code] = p.votes
  return out
}

export function blankVotes(): Record<string, number> {
  return Object.fromEntries(PARTIES.map((p) => [p.code, 0]))
}

export function createScenario(
  baseKind: BaseKind,
  baseVotes: Record<string, number>,
  name?: string,
): Scenario {
  const votes = { ...baseVotes }
  return {
    id: newId(),
    name: name ?? defaultName(baseKind),
    baseKind,
    votes,
    baseVotes: { ...baseVotes },
    updatedAt: new Date().toISOString(),
  }
}

function defaultName(kind: BaseKind): string {
  switch (kind) {
    case 'live':
      return 'Utifrån aktuellt resultat'
    case 'val-2022':
      return 'Utifrån valet 2022'
    default:
      return 'Tomt scenario'
  }
}

export function base2022(): Record<string, number> {
  return { ...ALE_2022_VOTES_BY_CODE }
}

/* ---- persistens ---- */

export function loadScenarios(): Scenario[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as Scenario[]) : []
  } catch {
    return []
  }
}

export function saveScenarios(list: Scenario[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(list))
  } catch {
    /* localStorage kan vara blockerat */
  }
}

export function exportScenario(s: Scenario): string {
  return JSON.stringify({ ...s, _format: 'val-ale-scenario-1' }, null, 2)
}

export function importScenario(json: string): Scenario {
  const raw = JSON.parse(json) as Partial<Scenario> & { votes?: Record<string, number> }
  if (!raw.votes || typeof raw.votes !== 'object') throw new Error('Ogiltig scenariofil')
  const votes: Record<string, number> = {}
  for (const [k, v] of Object.entries(raw.votes)) votes[k] = Math.max(0, Math.round(Number(v) || 0))
  return {
    id: newId(),
    name: typeof raw.name === 'string' ? raw.name : 'Importerat scenario',
    baseKind: (raw.baseKind as BaseKind) ?? 'blank',
    votes,
    baseVotes: raw.baseVotes ?? { ...votes },
    updatedAt: new Date().toISOString(),
  }
}
